import React from 'react'
import { Alert, Skeleton, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'

import type { VcnMigrationSummary } from '../../api/dto/vcn-migration-summary.dto'
import type { VcnDashboardMetric } from './types'
import {
  AsOf,
  Caption,
  Card,
  CardsRow,
  Chip,
  HeaderRow,
  HeaderTitle,
  Label,
  Section,
  Value,
} from './VcnManagementMigrationCards.styles'

/** antd Skeleton.Input sized to the metric number — matches the app's loading
 * convention (Skeleton, not a bespoke shimmer) and keeps the card height stable. */
const ValueSkeleton = (
  <Skeleton.Input active size="small" style={{ width: 72, minWidth: 0 }} />
)

/**
 * Chip colours, matching Card Mode's PILL_TONES so the same provider is the
 * same colour on both tabs. These now tint only the chip — the figure itself is
 * neutral, per the design.
 */
const ACCENT = {
  MARQETA: '#6a4ca8',
  WEX: '#2d5f9e',
  OVERLAP: '#0a939b',
  CLEANUP: '#b23f41',
} as const

/** Numeric metric keys only — excludes `asOf`, so `.toLocaleString()` is safe. */
type MetricValueKey = Exclude<keyof VcnMigrationSummary, 'asOf'>

interface CardConfig {
  key: string
  category: keyof typeof ACCENT
  chipLabel: string
  valueKey: MetricValueKey
  label: string
  /** Plain-language "how this number is counted" for the tooltip. */
  tooltip: string
  /**
   * The counting rule in the tile itself. States conditions, not what they
   * mean: a reader checking a number against the list wants to know what was
   * included, and "still to migrate" does not answer that.
   */
  caption: string
  /** null → display-only (not clickable). */
  metric: VcnDashboardMetric | null
}

/**
 * Short, because the tooltip now hangs off the info icon rather than the whole
 * tile — pointing at it is already deliberate. At 3s, covering the tile, nobody
 * ever saw the explanation.
 */
const TOOLTIP_MOUSE_ENTER_DELAY_SECONDS = 0.3

/**
 * What Benefit Mode shows (PM, 2026-09-15 demo).
 *
 * Was six tiles on two grains at once: two counted benefits, two counted cards,
 * and the pair sat side by side reading as though they were comparable. The
 * card-grained ones belong in Card Mode, where the row unit matches them and
 * the tiles are per-card by construction.
 *
 * What is left answers the one question this page exists for: how much of the
 * MARQETA-to-WEX migration is still outstanding. Both numbers count the same
 * thing under the same conditions — an active benefit, VCN as the primary
 * payment method, no test users — so the comparison between them is honest.
 *
 * The metrics behind the removed tiles are still wired — enum values, the EPS
 * summary branches, the list filters — so restoring one is a line in this array
 * plus a label. Unpicking that backend and putting it back would not be.
 */
const CARD_CONFIGS: CardConfig[] = [
  {
    key: 'marqeta-benefits',
    category: 'MARQETA',
    chipLabel: 'MARQETA',
    valueKey: 'marqetaBenefitIds',
    label: 'Benefits using MARQETA',
    caption: 'Active benefit · MARQETA card · VCN payment',
    tooltip:
      'Counts active benefits whose card is a real MARQETA card. Excludes benefits that have ended, test users, non-VCN payment methods, and any benefit whose card key matches no card. Clicking shows exactly these benefits.',
    metric: 'marqeta_benefits',
  },
  {
    key: 'wex-benefits',
    category: 'WEX',
    chipLabel: 'WEX',
    valueKey: 'wexBenefitIds',
    label: 'Benefits using WEX',
    caption: 'Active benefit · WEX card · VCN payment',
    tooltip:
      'Counts active benefits whose card is a real WEX card. Same conditions as the MARQETA figure — ended benefits, test users, non-VCN payment methods and unmatched card keys are all excluded — so the two numbers compare directly. Clicking shows exactly these benefits.',
    metric: 'wex_benefits',
  },
  {
    key: 'overlap',
    category: 'OVERLAP',
    chipLabel: 'OVERLAP',
    valueKey: 'overlap',
    label: 'Benefits running both cards',
    caption: 'Active benefit · employee holds an active card on both providers',
    tooltip:
      'Counts active benefits belonging to an employee who holds an active WEX card AND an active MARQETA card at the same time, counting the whole family’s cards. Unlike the two figures beside it, this counts benefits of those employees rather than benefits on a given provider, so it does not add up with them.',
    metric: 'overlap',
  },
]

function formatAsOf(asOf: string | null): string | null {
  if (!asOf) return null
  const date = new Date(asOf)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export interface VcnManagementMigrationCardsProps {
  summary?: VcnMigrationSummary
  isLoading: boolean
  error?: Error | null
  activeMetric: VcnDashboardMetric | null
  onSelect: (metric: VcnDashboardMetric) => void
}

export function VcnManagementMigrationCards({
  summary,
  isLoading,
  error = null,
  activeMetric,
  onSelect,
}: VcnManagementMigrationCardsProps) {
  const asOf = summary ? formatAsOf(summary.asOf) : null
  const showRefreshError = Boolean(error && summary)
  const showLoadError = Boolean(error && !summary)

  return (
    <Section>
      <HeaderRow>
        <HeaderTitle>VCN Migration Dashboard — Agreed Metrics</HeaderTitle>
        {asOf && <AsOf>As of {asOf}</AsOf>}
      </HeaderRow>
      {showRefreshError && (
        <Alert
          type="warning"
          message="Could not refresh migration metrics — showing last known data"
          description={error?.message}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {showLoadError && (
        <Alert
          type="warning"
          message="Could not load migration metrics"
          description={error?.message}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <CardsRow>
        {CARD_CONFIGS.map((config) => {
          const accent = ACCENT[config.category]
          const clickable = config.metric !== null
          const active =
            config.metric !== null && config.metric === activeMetric
          return (
            <Card
              key={config.key}
              type="button"
              $accent={accent}
              $active={active}
              $clickable={clickable}
              disabled={!clickable}
              aria-current={active ? 'true' : undefined}
              onClick={() => {
                if (config.metric !== null) onSelect(config.metric)
              }}
            >
              <Chip $accent={accent}>{config.chipLabel}</Chip>
              <Value>
                {summary ? (
                  summary[config.valueKey].toLocaleString()
                ) : isLoading ? (
                  ValueSkeleton
                ) : error ? (
                  <span style={{ color: '#8c8c8c' }}>—</span>
                ) : (
                  ValueSkeleton
                )}
              </Value>
              <Label>
                {config.label}
                <Tooltip
                  title={config.tooltip}
                  mouseEnterDelay={TOOLTIP_MOUSE_ENTER_DELAY_SECONDS}
                >
                  <InfoCircleOutlined
                    style={{ color: '#617d99', fontSize: 14 }}
                    aria-label={config.tooltip}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Tooltip>
              </Label>
              <Caption>{config.caption}</Caption>
            </Card>
          )
        })}
      </CardsRow>
    </Section>
  )
}
